import React, { useEffect, useRef, useState } from 'react';
import {
  useViewStateStore,
  ViewStateStoreApi
} from '../../stores/viewState.store';
import { Chat } from '../Messaging/Chat';
import { usePortletStore } from '../../App';
import {
  CallMenu,
  getName,
  useBeforeunload,
  useBigScreenBreakpoint,
  useCallMenuStore,
  useHeartBeat,
  useLiferaySessionExtender,
  useVideoTileResizer,
  useWakeLock
} from 'virtual-visit-common';
import {
  Button,
  getPageLogoUrl,
  intl,
  NotificationType,
  notifyCustom
} from 'ehs-react';
import { useChatStore } from '../../stores/chat.store';
import { CallStateApi, useCallStore } from '../../stores/call.store';
import { getCallStrategy } from '../../call/callstrategy';
import {
  useWaitingRoomStore,
  WaitingRoomStoreApi
} from '../../stores/waitingRoom.store';

interface ConferenceProps {
  toggleVideoChatView: () => void;
}

export function VideoConference(): JSX.Element {
  const [videoOrChat, setVideoOrChat] = useState('video');
  const [init] = useState(true);
  const [sendVisitStatus] = useWaitingRoomStore(state => [
    state.sendVisitStatus
  ]);
  const [webSocketPingInterval] = usePortletStore(state => [
    state.configuration.webSocketConfig.webSocketPingInterval
  ]);
  useHeartBeat(sendVisitStatus, webSocketPingInterval);
  useLiferaySessionExtender(30000, init);
  useBeforeunload(evt => {
    evt.preventDefault();
    evt.stopImmediatePropagation();
    const retVal = 'Do you really want to exit?';
    evt.returnValue = retVal;
    return retVal;
  }, init);

  useWakeLock(videoOrChat === 'chat');

  const [enableChatDuringCall, videoProvider] = usePortletStore(state => [
    state.configuration.enableChatDuringCall,
    state.configuration.videoProvider
  ]);

  const [resetUnreadMessaged, numberOfUnreadMessages] = useChatStore(state => [
    state.resetUnreadMessages,
    state.numberOfUnreadMessages
  ]);

  const [guestToken] = useViewStateStore(state => [
    state.viewStateData?.guestData?.urlToken
  ]);

  const bigScreen = useBigScreenBreakpoint(1500);
  useEffect(() => {
    console.log(
      'nofification reset',
      bigScreen,
      videoOrChat,
      numberOfUnreadMessages
    );
    if ((bigScreen || videoOrChat === 'chat') && numberOfUnreadMessages > 0) {
      resetUnreadMessaged();
    }
  }, [bigScreen, resetUnreadMessaged, videoOrChat, numberOfUnreadMessages]);

  const toggleVideoOrChat = () => {
    console.log('toggle video or chat');
    if (videoOrChat === 'video') {
      setVideoOrChat('chat');
    } else if (videoOrChat === 'chat') {
      resetUnreadMessaged();
      setVideoOrChat('video');
    }
  };

  const videoDisplayCls =
    videoOrChat === 'video' ? '' : ' vvpp-video-chat-hidden';
  const chatDisplayCls =
    videoOrChat === 'chat' ? '' : ' vvpp-video-chat-hidden';

  return (
    <>
      <div className={'vvpp-video-chat' + videoDisplayCls}>
        <VideoConferenceWrapper toggleVideoChatView={toggleVideoOrChat} />
      </div>
      {enableChatDuringCall && !guestToken && (
        <div className={'vvpp-video-chat' + chatDisplayCls}>
          {videoProvider === 'AZURE' && (
            <div className="vvpp-call-header-chat">
              <CallHeader />
            </div>
          )}
          <ChatWrapper onBackButtonClick={toggleVideoOrChat} />
        </div>
      )}
    </>
  );
}

function VideoConferenceWrapper(props: ConferenceProps): JSX.Element {
  const [startCall, callStrategyConfig, callRunning] = useCallStore(state => [
    state.startCall,
    state.callStrategyConfig,
    state.callRunning
  ]);
  const [visitInfo] = useWaitingRoomStore(state => [state.visitInfo]);
  const videoProvider = usePortletStore(
    state => state.configuration.videoProvider
  );

  useEffect(() => {
    CallStateApi.setState({
      callRunning: false
    });
  }, []);

  useEffect(() => {
    if (callStrategyConfig && visitInfo && !callRunning) {
      startCall();
    }
  }, [startCall, callStrategyConfig, visitInfo, callRunning]);

  if (videoProvider === 'AZURE') {
    return <AzureVideoConference {...props} />;
  } else if (videoProvider === 'JITSI') {
    return <JitsiVideoConference {...props} />;
  } else {
    return <div>Unkown provider!!!</div>;
  }
}

function AzureVideoConference(props: ConferenceProps): JSX.Element {
  const [callStatusMessage] = useCallStore(state => [state.callStatusMessage]);
  const [toggleCamera, cameras, cameraMuted] = useCallMenuStore(state => [
    state.toggleCamera,
    state.cameras,
    state.cameraMuted
  ]);

  useEffect(() => {
    const msgContainer = document.getElementById('vvpp-call-status-message');
    if (msgContainer) {
      msgContainer.innerHTML = callStatusMessage || '';
    }
  }, [callStatusMessage]);

  const containerRef = useRef<HTMLDivElement>(null);
  useVideoTileResizer(containerRef);

  useGuestStatusChangeHandler();

  const toggleCamHiddenCls =
    cameras && cameras.length > 1 ? '' : ' vvpp-hidden';

  return (
    <div className="vvpp-video-panel">
      <CallHeader />
      <div
        id="vv-video-container"
        className={'vvpp-video-container'}
        ref={containerRef}
      >
        <div
          id="vvpp-call-status-message"
          className="vvpp-call-status-message"
        />
        <div id="localVideo" className="vvpp-video-local">
          {!cameraMuted && (
            <div
              className={'vvpp-toggle-camera ehealthicon-toggle-camera'}
              onClick={toggleCamera}
              // needed because of sonar complains
              /* eslint-disable-next-line @typescript-eslint/no-empty-function */
              onKeyDown={() => {}}
            />
          )}
        </div>
        <div id="ScreenSharing" className="vvpp-screen-sharing" />
      </div>
      <CallMenuWrapper {...props} />
    </div>
  );
}

function useGuestStatusChangeHandler() {
  const [currentGuestStatus] = useWaitingRoomStore(store => [
    store.currentGuestParticipant?.status
  ]);
  const [stopCall] = useCallStore(store => [store.stopCall]);

  useEffect(() => {
    if (currentGuestStatus === 'INVALID_REJECTED') {
      //stop the call
      getCallStrategy().setPreventPhysicianLeftNotification(true);
      getCallStrategy().stopCall();
      sessionStorage.clear();
      //go to final screen
      console.log('trigger next state from VideoConference');
      ViewStateStoreApi.getState().nextState();
    }
  }, [currentGuestStatus, stopCall]);
}

function CallHeader(): JSX.Element {
  const [institutionName] = useViewStateStore(state => [
    state.viewStateData?.selectedInstitution?.name
  ]);
  const [stopCall] = useCallStore(state => [state.stopCall]);
  const [loggedInPatient] = useViewStateStore(state => [
    state.viewStateData?.loggedInPatient
  ]);

  return (
    <div className={'vvpp-call-header'}>
      {!loggedInPatient && (
        <div className="vvpp-call-header-logo">
          <img src={getPageLogoUrl()} alt="logo" />
        </div>
      )}
      <div className="vvpp-call-header-name">
        <div>{intl('virtualvisitpatientportlet.title')}</div>
      </div>
      <Button
        id="vvpp-call-header-hangup-button"
        className="vvpp-call-header-hangup"
        text={intl('virtualvisitpatientportlet.video.endcall')}
        icon="finish-call"
        onClick={stopCall}
        variant="gray"
      />
    </div>
  );
}

function JitsiVideoConference(props: ConferenceProps): JSX.Element {
  const [numberOfUnreadMessages] = useChatStore(state => [
    state.numberOfUnreadMessages
  ]);
  return (
    <div className="vvpp-video-container vvpp-jitsi-video-container">
      <div id="jitsi-video-conference" className="vvpp-video-remote" />
      <div
        className="vvpp-open-chat ehealthicon-chat"
        onClick={props.toggleVideoChatView}
      />
      {numberOfUnreadMessages > 0 && (
        <div className="vvpp-number-of-unread-chat-messages vvpp-unread-messages-jitsi">
          {numberOfUnreadMessages}
        </div>
      )}
    </div>
  );
}

function CallMenuWrapper(props: ConferenceProps): JSX.Element {
  const [numberOfUnreadMessages] = useChatStore(state => [
    state.numberOfUnreadMessages
  ]);
  const [enableScreenSharing] = usePortletStore(state => [
    state.configuration.enableScreenSharing
  ]);
  const [participantList] = useWaitingRoomStore(state => [state.participants]);
  const [patientData] = useWaitingRoomStore(state => [
    state.visitInfo?.patientData
  ]);

  const [guestUrlToken] = useViewStateStore(state => [
    state.viewStateData?.guestData?.urlToken
  ]);

  const patientName = getName(
    patientData?.givenName || '',
    patientData?.familyName || ''
  );
  const [physicianData] = useWaitingRoomStore(state => [
    state.visitInfo?.visit?.user
  ]);
  const physicianName =
    physicianData?.fullname ||
    intl('virtualvisitoverviewportlet.chat.tab.physician');

  const [maxGuestsAllowed] = usePortletStore(state => [
    state.configuration.maxGuests
  ]);
  const groupCalls = maxGuestsAllowed > 0;

  return (
    <div className="vvpp-call-menu-wrapper">
      <CallMenu
        callMenuInterface={getCallStrategy().getCallMenuInterface()!}
        numberOfUnreadMessages={numberOfUnreadMessages}
        showCallTimer={false}
        enableScreenSharing={enableScreenSharing}
        changeVideoChatView={
          guestUrlToken ? undefined : props.toggleVideoChatView
        }
        participants={groupCalls ? participantList : undefined}
        patientName={patientName}
        physicianName={physicianName}
        className={'vvpp-call-menu'}
      />
    </div>
  );
}

export function ChatWrapper(props: {
  onBackButtonClick: () => void;
}): JSX.Element {
  const [
    chatMessages,
    sendChatMessage,
    loadChatNotificationCountFromSession,
    init,
    dispose
  ] = useChatStore(state => [
    state.chatMessages,
    state.sendChatMessage,
    state.loadChatNotificationCountFromSession,
    state.init,
    state.dispose
  ]);

  useEffect(() => {
    init();
    loadChatNotificationCountFromSession();
    return () => {
      dispose();
    };
  }, [init, dispose, loadChatNotificationCountFromSession]);

  return (
    <Chat
      chatMessages={chatMessages}
      sendChatMessage={sendChatMessage}
      toggleVideoChatView={props.onBackButtonClick}
    />
  );
}

export function showBioAnalysisConfirmationDialog() {
  const content = intl(
    'virtualvisitpatientportlet.bioanalysis.start.confirmation.content'
  );
  notifyCustom(closingCallback => {
    return {
      closeOnOverlayClicked: false,
      type: NotificationType.INFO,
      title: intl(
        'virtualvisitpatientportlet.bioanalysis.start.confirmation.title'
      ),
      content: <div>{content}</div>,
      actions: (
        <>
          <Button
            variant="primary"
            text={intl(
              'virtualvisitpatientportlet.bioanalysis.start.confirmation.allow'
            )}
            onClickCapture={() => {
              WaitingRoomStoreApi.getState().grantBioAnalysisPermission();
              closingCallback(undefined)();
            }}
          />
          <Button
            variant="gray"
            text={intl(
              'virtualvisitpatientportlet.bioanalysis.start.confirmation.deny'
            )}
            onClickCapture={() => closingCallback(undefined)()}
          />
        </>
      )
    };
  });
}
